import { FC, useState, useMemo, useEffect } from "react";
import { Dialog, TextField } from "@blofin/blofin-ui";
import { useTranslations } from "use-intl";
import { SubAccountResponse } from "./type";
import { stylesMainAccount, stylesSubAccount, stylesCurrent, stylesArrowIcon } from "./styles";
import useSwitchAccount from "../hooks/useSwitchAccount";
import { useStoreState } from "../store/useStore";
import { clipImage } from "../utils";

interface SwitchDialogProps {
  open: boolean;
  onClose: () => void;
  subAccountInfo: SubAccountResponse | null;
}

const SwitchDialog: FC<SwitchDialogProps> = (props) => {
  const { open, onClose, subAccountInfo } = props;

  const t = useTranslations("header");

  const { userInfo, theme, showTheme } = useStoreState();

  const realTheme = useMemo(() => {
    return showTheme ? theme : "light";
  }, [theme, showTheme]);

  const { main_account_info: mainAccount, sub_account_list: subAccounts } = subAccountInfo || {
    main_account_info: {},
    sub_account_list: []
  };

  const { switchTo } = useSwitchAccount();

  const [searchValue, setSearchValue] = useState("");

  const arrowIcon = (isCurrent: boolean) => {
    return isCurrent ? (
      <div className={stylesCurrent({ mode: realTheme })}>{t("current")}</div>
    ) : (
      <div className="iconfont icon-arrow-down-l-line text-[24px] text-light-label dark:text-dark-label transform rotate-[-90deg]"></div>
    );
  };

  const isMainAccount = useMemo(() => {
    if (!userInfo || !mainAccount) {
      return false;
    }
    return Number(userInfo?.uid) === Number(mainAccount?.uid);
  }, [userInfo, mainAccount]);

  const subAccountsFilter = useMemo(() => {
    return subAccounts.filter((item) => {
      return item.user_name?.includes(searchValue) && item.switch_state === 0;
    });
  }, [subAccounts, searchValue]);

  const onAccountClick = (type: "main" | "sub", subUid: string, toName: string) => {
    if (isMainAccount && type === "main") {
      onClose();
    }
    if (!isMainAccount && type === "main") {
      switchTo({ type: "SUB_TO_MAIN", toName });
    }
    if (isMainAccount && type === "sub") {
      switchTo({ type: "MAIN_TO_SUB", to_sub_uid: subUid, toName });
    }
    if (!isMainAccount && type === "sub") {
      if (!subUid || Number(userInfo?.uid) === Number(subUid)) {
        onClose();
        return;
      }
      switchTo({ type: "SUB_TO_SUB", to_sub_uid: subUid, toName });
    }
  };

  useEffect(() => {
    if (!open) {
      setSearchValue("");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      cancel={onClose}
      size="small"
      title={t("swichAccount")}
      footer={null}
      theme={realTheme}
      className="!w-[440px]"
      content={
        <div className="flex flex-col gap-4 pt-[10px]">
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => onAccountClick("main", "", mainAccount.email || "")}
          >
            <div className="flex justify-start items-center">
              <div className="w-[35px] h-[35px] mr-[4px] rounded-full overflow-hidden">
                <img
                  src={clipImage(mainAccount.profile, 70, 70)}
                  alt=""
                  className="w-full h-full"
                />
              </div>
              <div>
                <div
                  className={`${stylesMainAccount({ mode: realTheme })} text-[14px] leading-[20px] font-medium`}
                >
                  {t("mainAccount")}
                </div>
                <div
                  dir="ltr"
                  className={`${stylesSubAccount({ mode: realTheme })} text-[12px] leading-[18px] mt-[2px]`}
                >
                  {mainAccount.email}
                </div>
              </div>
            </div>
            <div>{arrowIcon(isMainAccount)}</div>
          </div>
          <TextField
            variant="filled"
            theme={realTheme}
            type="text"
            className="h-[34px]"
            startAdornment={<span className={stylesArrowIcon({ mode: realTheme })}></span>}
            placeholder={t("subAccountName")}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <div className={`${stylesSubAccount({ mode: realTheme })} text-[14px] leading-[20px]`}>
            {t("subAccounts")}
          </div>
          <ul className="flex flex-col gap-4">
            {subAccountsFilter.map((item) => {
              const isCurrent = Number(userInfo?.uid) === Number(item.sub_uid);
              return (
                <li
                  className="flex justify-between items-center cursor-pointer"
                  key={item.sub_uid}
                  onClick={() => onAccountClick("sub", item.sub_uid, item.user_name)}
                >
                  <div className="flex flex-col justify-center h-[48px]">
                    <p
                      dir="ltr"
                      className={`text-[14px] leading-[20px] font-medium ${stylesMainAccount({ mode: realTheme })}`}
                    >
                      {item.user_name}
                    </p>
                    <p
                      className={`text-[12px] leading-[18px] mt-[2px] ${stylesSubAccount({ mode: realTheme })}`}
                    >
                      {item.sub_user_type === "NORMAL_SUB_ACCOUNT" ? t("standardSubAccount") : null}
                    </p>
                  </div>
                  <div>{arrowIcon(isCurrent)}</div>
                </li>
              );
            })}
          </ul>
        </div>
      }
    />
  );
};

export default SwitchDialog;
